import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AppShell } from '../components/layout/AppShell';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { PlatformIcon } from '../components/common/PlatformIcon';
import { integrationsApi } from '../lib/api';
import {
  ExternalLink, Copy, Check, Settings, Shield, Globe, Smartphone,
  ChevronRight, AlertCircle, CheckCircle2, Key, Trash2, Eye, EyeOff,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };
  return (
    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={handleCopy}>
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
}

function PlatformCard({ platform, onConfigure }) {
  return (
    <Card
      className={`group cursor-pointer transition-all duration-150 hover:shadow-md ${
        platform.has_credentials
          ? 'border-emerald-200 dark:border-emerald-800'
          : 'hover:border-primary/40'
      }`}
      onClick={() => onConfigure(platform)}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <PlatformIcon platform={platform.platform} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-sm">{platform.name}</h3>
              {platform.has_credentials ? (
                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  Not Configured
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              {platform.setup_type === 'mobile' ? (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Smartphone className="w-3 h-3" /> Mobile / Messaging
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Globe className="w-3 h-3" /> Web Developer Portal
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
      </CardContent>
    </Card>
  );
}

function PlatformConfigDialog({ platform, open, onClose }) {
  const queryClient = useQueryClient();
  const [credentials, setCredentials] = useState({});
  const [showPasswords, setShowPasswords] = useState({});

  const { data: detail } = useQuery({
    queryKey: ['platform-detail', platform?.platform],
    queryFn: () => integrationsApi.getPlatformDetail(platform.platform).then((r) => r.data),
    enabled: !!platform?.platform && open,
  });

  React.useEffect(() => {
    if (detail?.credentials) {
      setCredentials(detail.credentials);
    } else {
      setCredentials({});
    }
  }, [detail]);

  const saveMutation = useMutation({
    mutationFn: (creds) => integrationsApi.saveCredentials(platform.platform, creds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platforms'] });
      queryClient.invalidateQueries({ queryKey: ['platform-detail', platform.platform] });
      toast.success(`${platform.name} credentials saved!`);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => integrationsApi.deleteCredentials(platform.platform),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platforms'] });
      queryClient.invalidateQueries({ queryKey: ['platform-detail', platform.platform] });
      setCredentials({});
      toast.success('Credentials removed');
    },
  });

  const handleSave = () => {
    saveMutation.mutate(credentials);
  };

  const togglePassword = (key) => {
    setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!platform) return null;

  const allFields = [...(detail?.required_fields || platform.required_fields || []), ...(detail?.optional_fields || platform.optional_fields || [])];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <PlatformIcon platform={platform.platform} size="md" />
            <div>
              <DialogTitle className="text-lg">{platform.name} Integration</DialogTitle>
              {platform.has_credentials && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Credentials saved
                </p>
              )}
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[65vh] pr-4">
          <Tabs defaultValue="setup" className="w-full">
            <TabsList className="mb-4 w-full">
              <TabsTrigger value="setup" className="flex-1">Setup Steps</TabsTrigger>
              <TabsTrigger value="credentials" className="flex-1">API Credentials</TabsTrigger>
            </TabsList>

            {/* Setup Steps Tab */}
            <TabsContent value="setup" className="space-y-4">
              {platform.callback_url && (
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2 mb-2">
                      <Shield className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold">Callback / Redirect URL</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Paste this URL in the developer portal's "Redirect URI" or "Callback URL" field
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 bg-background rounded-md border p-2">
                      <code className="text-xs flex-1 break-all font-mono select-all">{platform.callback_url}</code>
                      <CopyButton text={platform.callback_url} />
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Developer Portal</span>
                    </div>
                    <a
                      href={platform.developer_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      {platform.developer_url} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Setup Steps
                </h4>
                <div className="space-y-2">
                  {(detail?.setup_steps || platform.setup_steps || []).map((step, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-sm">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {platform.scopes && platform.scopes.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Key className="w-4 h-4" /> Required Permissions (Scopes)
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {platform.scopes.map((scope) => (
                      <code key={scope} className="text-xs bg-muted px-2 py-1 rounded-md font-mono">
                        {scope}
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Credentials Tab */}
            <TabsContent value="credentials" className="space-y-4">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  API credentials are stored securely on the server. When your domain changes, update APP_URL in the .env file and callback URLs will update automatically.
                </p>
              </div>

              {allFields.map((field) => {
                const isRequired = (detail?.required_fields || platform.required_fields || []).some((f) => f.key === field.key);
                return (
                  <div key={field.key} className="space-y-2">
                    <Label className="flex items-center gap-1">
                      {field.label}
                      {isRequired && <span className="text-destructive">*</span>}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                        value={credentials[field.key] || ''}
                        onChange={(e) => setCredentials((prev) => ({ ...prev, [field.key]: e.target.value }))}
                        placeholder={field.label}
                        data-testid={`credential-${field.key}`}
                      />
                      {field.type === 'password' && (
                        <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => togglePassword(field.key)}>
                          {showPasswords[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </TabsContent>
          </Tabs>
        </ScrollArea>

        <Separator className="my-2" />

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          <div>
            {platform.has_credentials && (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
                <Trash2 className="w-4 h-4 mr-1" /> Remove Credentials
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => onClose(false)}>Close</Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving...' : 'Save Credentials'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function IntegrationsPage() {
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['platforms'],
    queryFn: () => integrationsApi.getPlatforms().then((r) => r.data),
  });

  const handleConfigure = (platform) => {
    setSelectedPlatform(platform);
    setDialogOpen(true);
  };

  const webPlatforms = data?.platforms?.filter((p) => p.setup_type === 'web') || [];
  const mobilePlatforms = data?.platforms?.filter((p) => p.setup_type === 'mobile') || [];
  const configuredCount = data?.platforms?.filter((p) => p.has_credentials).length || 0;

  return (
    <AppShell
      breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Platform Integrations' },
      ]}
    >
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold font-display tracking-tight">Platform Integrations</h1>
          <p className="text-sm text-muted-foreground mt-1">Connect social media platforms to enable real publishing</p>
        </div>

        <Card className="bg-muted/50">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm font-medium">
                  <span className="text-2xl font-display font-semibold tabular-nums mr-2">{configuredCount}</span>
                  / {data?.platforms?.length || 8} platforms configured
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  APP URL: <code className="bg-background px-1.5 py-0.5 rounded text-xs font-mono">{data?.app_url || '...'}</code>
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <AlertCircle className="w-3 h-3" />
                Update APP_URL in .env when switching domains
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Web Platforms */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-base font-semibold">Web-Based Platforms</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isLoading
              ? [1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />)
              : webPlatforms.map((platform) => (
                  <PlatformCard key={platform.platform} platform={platform} onConfigure={handleConfigure} />
                ))}
          </div>
        </div>

        {/* Mobile Platforms */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-base font-semibold">Mobile / Messaging Platforms</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {mobilePlatforms.map((platform) => (
              <PlatformCard key={platform.platform} platform={platform} onConfigure={handleConfigure} />
            ))}
          </div>
        </div>

        {/* Important Notes */}
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-500" /> Important Notes
            </h3>
            <ul className="text-xs text-muted-foreground space-y-1.5">
              <li>Callback URLs are generated automatically based on the <strong>APP_URL</strong> variable</li>
              <li>When switching to your own domain, update APP_URL in the <code className="bg-background px-1 rounded">.env</code> file</li>
              <li>Meta (Facebook + Instagram + Threads) uses the same developer app — configure once</li>
              <li>Telegram does not require OAuth — only a Bot Token is needed</li>
              <li>Don't forget to add the Callback URLs from this panel to each platform's developer portal</li>
              <li>API credentials are stored securely on the server</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <PlatformConfigDialog platform={selectedPlatform} open={dialogOpen} onClose={setDialogOpen} />
    </AppShell>
  );
}
